import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import { Image as ImageIcon, Loader2, MessageCircle, Minus, Paperclip, Phone, Plus, Search, Send, Video, X } from "lucide-react";
import {
    chatConnectClient,
    connectBaseUrl,
    normalizeUserId,
    type ChatDm,
    type ChatMessage,
    type DirectoryUser
} from "./connectClient";
import { useAccessToken } from "@/auth/useAccessToken";
import { useDraggable } from "./useDraggable";
import { playMessageTone } from "@athavita/ui-theme";
import { pushDesktopNotification } from "@/lib/useDesktopShell";
import { sanitizeHtml } from "@/lib/sanitizeHtml";

// Custom-event bridge — the Gmail-style roster in FolderPane fires this
// when a user row is clicked. We could thread a context provider through
// AppShell but the event is dispatched once-per-click and doesn't
// warrant the ceremony. Callers:
//   window.dispatchEvent(new CustomEvent(OPEN_DM_EVENT, { detail: { userId } }))
export const OPEN_DM_EVENT = "athavita:openDm";
export interface OpenDmDetail {
    userId: string;
    /** Optional peer hints from the roster — short-circuits the by-id
     *  lookup so a Call button works the instant the DM opens. */
    email?: string;
    displayName?: string;
}

/** Hook: look up a user's display name, with a shared react-query cache. */
function usePeerName(userId: string | undefined) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["chat-peer", userId, token],
        queryFn: () => (userId ? chatConnectClient.lookupUser(userId, token) : Promise.resolve(null)),
        enabled: Boolean(userId && token),
        staleTime: 5 * 60_000
    });
}

/**
 * Gmail-style chat dock — a minimisable card anchored bottom-right of the
 * Mail SPA. Lets the user search the cross-tenant directory, start a DM
 * with anyone on the platform (intra-org or cross-org), and exchange
 * messages without leaving their inbox. All traffic goes through the
 * Connect BFF.
 */
export function ChatDock() {
    const [expanded, setExpanded] = useState(false);
    const [activeDm, setActiveDm] = useState<ChatDm | null>(null);
    const [, setPeerHint] = useState<{ userId: string; email?: string; displayName?: string } | null>(null);
    const [searchOpen, setSearchOpen] = useState(false);
    const token = useAccessToken();
    const qc = useQueryClient();
    const auth = useAuth();
    const myId = normalizeUserId(auth.user?.profile.sub);

    // Real-time SignalR connection to the Connect BFF. Listens for
    // `dm.message.sent` and invalidates the affected DM's message cache
    // so the recipient sees the message instantly instead of waiting
    // for the 5-second polling tick. Also re-fetches the DM list + the
    // Gmail-style roster so a brand-new chat appears the moment it
    // lands, without the 15s roster poll.
    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        let connection: HubConnection | null = null;

        const start = async () => {
            const built = new HubConnectionBuilder()
                .withUrl(`${connectBaseUrl}/hubs/connect`, {
                    accessTokenFactory: () => token
                })
                .withAutomaticReconnect()
                .configureLogging(LogLevel.Warning)
                .build();

            // Suppress "No client method found" warnings for events the
            // Connect hub broadcasts to all subscribers. Mail-web only
            // acts on dm.message.sent; the rest are handled by connect-web.
            for (const evt of [
                "presence.changed",
                "channel.message.sent",
                "channel.message.edited",
                "channel.message.deleted",
                "channel.message.pinned",
                "channel.message.reaction",
                "channel.typing.started",
                "channel.typing.stopped"
            ]) {
                built.on(evt, () => { /* handled by connect-web */ });
            }

            built.on("dm.message.sent", (payload: {
                chatId: string;
                participantA?: string;
                participantB?: string;
                message: ChatMessage;
            }) => {
                void qc.invalidateQueries({ queryKey: ["dm-messages", payload.chatId] });
                void qc.invalidateQueries({ queryKey: ["chat-dms"] });
                void qc.invalidateQueries({ queryKey: ["chat-roster-dms"] });
                // Determine if the message is from the OTHER person. Compare
                // normalised forms so dashed vs no-dash GUIDs don't trick
                // the check (this is the same fix that flipped roster
                // self-display).
                const author = normalizeUserId(payload.message.authorUserId);
                const fromOther = Boolean(author) && author !== myId;
                if (!fromOther) return;

                // Receiver-only: ding the tone and pop the chat window
                // open if it's not already on this DM. Sender's own tab
                // never enters this branch, so no echo on send.
                try { playMessageTone(); } catch { /* autoplay blocked */ }

                // Desktop toast — only when running inside the Electron
                // shell AND the window isn't currently focused, so the
                // user actually needs the OS-level interrupt. No-op in
                // browser tabs and when the window already has focus.
                if (typeof document !== "undefined" && !document.hasFocus()) {
                    const preview = (payload.message.bodyHtml ?? "")
                        .replace(/<[^>]*>/g, "")
                        .slice(0, 140);
                    pushDesktopNotification("New chat — Athavita", preview || "You have a new message.");
                }

                if (payload.participantA && payload.participantB) {
                    setActiveDm((current) => {
                        if (current && current.id === payload.chatId) return current;
                        return {
                            id: payload.chatId,
                            participantA: payload.participantA!,
                            participantB: payload.participantB!
                        };
                    });
                    const peerId = normalizeUserId(payload.participantA) === myId
                        ? payload.participantB
                        : payload.participantA;
                    setPeerHint({ userId: peerId });
                }
            });

            try {
                await built.start();
                if (cancelled) {
                    await built.stop();
                    return;
                }
                connection = built;
            } catch (err) {
                console.warn("Connect hub start failed", err);
            }
        };

        void start();
        return () => {
            cancelled = true;
            if (connection && connection.state !== HubConnectionState.Disconnected) {
                void connection.stop();
            }
        };
    }, [token, qc, myId]);

    // External open trigger — the roster in FolderPane dispatches a
    // CustomEvent with the target userId; we start the DM lazily here
    // (idempotent on the BFF side, so duplicate open clicks are safe).
    useEffect(() => {
        const handler = async (e: Event) => {
            const detail = (e as CustomEvent<OpenDmDetail>).detail;
            if (!detail?.userId || !token) return;
            try {
                const dm = await chatConnectClient.startDm(detail.userId, token);
                setActiveDm(dm);
                setPeerHint({ userId: detail.userId, email: detail.email, displayName: detail.displayName });
                setExpanded(false);
            } catch { /* ignore — roster shows the error inline */ }
        };
        window.addEventListener(OPEN_DM_EVENT, handler as EventListener);
        return () => window.removeEventListener(OPEN_DM_EVENT, handler as EventListener);
    }, [token]);

    // Roster "+" button opens the directory search card. Separate event
    // so future entry points (e.g. a Cmd+K palette) can share the same
    // surface without tangling props.
    useEffect(() => {
        const handler = () => {
            setExpanded(true);
            setSearchOpen(true);
        };
        window.addEventListener("athavita:openChatSearch", handler);
        return () => window.removeEventListener("athavita:openChatSearch", handler);
    }, []);

    return (
        <div className="pointer-events-none fixed bottom-0 right-4 z-30 flex items-end gap-3">
            {activeDm ? (
                <ChatWindow
                    dm={activeDm}
                    onClose={() => setActiveDm(null)}
                    onMinimize={() => setActiveDm(null)}
                />
            ) : null}
            {expanded ? (
                <div className="pointer-events-auto mb-3">
                    <DockCard
                        onClose={() => {
                            setExpanded(false);
                            setSearchOpen(false);
                        }}
                        onOpenDm={(dm) => {
                            setActiveDm(dm);
                            setExpanded(false);
                        }}
                        searchOpen={searchOpen}
                        setSearchOpen={setSearchOpen}
                    />
                </div>
            ) : null}
        </div>
    );
}

function DockCard({
    onClose,
    onOpenDm,
    searchOpen,
    setSearchOpen
}: {
    onClose: () => void;
    onOpenDm: (dm: ChatDm) => void;
    searchOpen: boolean;
    setSearchOpen: (v: boolean) => void;
}) {
    const token = useAccessToken();
    const dms = useQuery({
        queryKey: ["chat-dms", token],
        queryFn: () => chatConnectClient.listDms(token),
        enabled: Boolean(token),
        refetchInterval: 15_000
    });

    return (
        <div className="mb-0 flex w-80 flex-col rounded-t-lg border bg-card shadow-2xl">
            <header className="flex items-center justify-between border-b px-3 py-2">
                <span className="text-sm font-semibold">Chat</span>
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => setSearchOpen(!searchOpen)}
                        className="flex h-7 w-7 items-center justify-center rounded hover:bg-accent"
                        aria-label="New chat"
                    >
                        <Plus className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex h-7 w-7 items-center justify-center rounded hover:bg-accent"
                        aria-label="Close chat"
                    >
                        <Minus className="h-4 w-4" />
                    </button>
                </div>
            </header>
            {searchOpen ? (
                <DirectorySearch
                    onPick={async (u) => {
                        if (!token) return;
                        const dm = await chatConnectClient.startDm(u.userId, token);
                        setSearchOpen(false);
                        onOpenDm(dm);
                    }}
                />
            ) : null}
            <div className="max-h-80 min-h-[6rem] overflow-auto">
                {dms.isPending ? (
                    <p className="p-4 text-xs text-muted-foreground">Loading…</p>
                ) : dms.data && dms.data.length > 0 ? (
                    <ul className="divide-y">
                        {dms.data.map((dm) => (
                            <DmRow key={dm.id} dm={dm} onOpen={() => onOpenDm(dm)} />
                        ))}
                    </ul>
                ) : (
                    <p className="p-4 text-xs text-muted-foreground">
                        No chats yet — use <span className="font-medium">+</span> to find someone.
                    </p>
                )}
            </div>
        </div>
    );
}

function DirectorySearch({ onPick }: { onPick: (u: DirectoryUser) => void | Promise<void> }) {
    const token = useAccessToken();
    const [q, setQ] = useState("");
    const [highlight, setHighlight] = useState(0);

    const search = useQuery({
        queryKey: ["directory", q, token],
        queryFn: () =>
            q.trim().length >= 2
                ? chatConnectClient.searchDirectory(q.trim(), token)
                : Promise.resolve([] as DirectoryUser[]),
        enabled: Boolean(token)
    });

    useEffect(() => setHighlight(0), [search.data]);

    const results = search.data ?? [];

    return (
        <div className="border-b p-2">
            <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <input
                    value={q}
                    autoFocus
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => {
                        if (results.length === 0) return;
                        if (e.key === "ArrowDown") {
                            e.preventDefault();
                            setHighlight((h) => (h + 1) % results.length);
                        } else if (e.key === "ArrowUp") {
                            e.preventDefault();
                            setHighlight((h) => (h - 1 + results.length) % results.length);
                        } else if (e.key === "Enter") {
                            e.preventDefault();
                            void onPick(results[highlight] ?? results[0]!);
                        }
                    }}
                    placeholder="Find someone by name or email"
                    className="h-8 w-full rounded-md border pl-7 pr-2 text-sm outline-none focus:ring-2 focus:ring-ring"
                />
            </div>
            {results.length > 0 ? (
                <ul className="mt-2 max-h-48 overflow-auto rounded-md border bg-popover">
                    {results.map((u, idx) => (
                        <li key={u.userId}>
                            <button
                                type="button"
                                onMouseEnter={() => setHighlight(idx)}
                                onClick={() => void onPick(u)}
                                className={`flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm ${
                                    idx === highlight ? "bg-accent" : "hover:bg-muted"
                                }`}
                            >
                                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold">
                                    {(u.displayName ?? u.email).slice(0, 2).toUpperCase()}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="truncate">{u.displayName ?? u.email}</div>
                                    <div className="truncate text-[10px] text-muted-foreground">
                                        {u.email}
                                    </div>
                                </div>
                            </button>
                        </li>
                    ))}
                </ul>
            ) : q.trim().length >= 2 && !search.isPending ? (
                <p className="mt-2 px-2 text-xs text-muted-foreground">No matches.</p>
            ) : null}
        </div>
    );
}

function ChatWindow({
    dm,
    onClose,
    onMinimize
}: {
    dm: ChatDm;
    onClose: () => void;
    onMinimize: () => void;
}) {
    const token = useAccessToken();
    const qc = useQueryClient();
    const msgs = useQuery({
        queryKey: ["dm-messages", dm.id, token],
        queryFn: () => chatConnectClient.listDmMessages(dm.id, token),
        enabled: Boolean(token),
        refetchInterval: 5_000
    });
    const send = useMutation({
        mutationFn: (bodyHtml: string) => chatConnectClient.sendDmMessage(dm.id, bodyHtml, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["dm-messages", dm.id] })
    });
    const [draft, setDraft] = useState("");
    const scrollerRef = useRef<HTMLDivElement | null>(null);
    const lastSeenCount = useRef<number | null>(null);

    const auth = useAuth();
    const myId = normalizeUserId(auth.user?.profile.sub);

    useEffect(() => {
        const el = scrollerRef.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
    }, [msgs.data?.length]);

    // The dock-level SignalR handler already plays the tone for incoming
    // messages and pops the right window open. Here we just track the
    // count so future enhancements (unread badges, jump-to-bottom prompt)
    // have a hook — no tone is played here to avoid double-dings.
    useEffect(() => {
        const count = msgs.data?.length ?? 0;
        if (lastSeenCount.current === null) {
            lastSeenCount.current = count;
            return;
        }
        lastSeenCount.current = count;
    }, [msgs.data, myId]);
    const peerId = useMemo(() => otherParticipantId(dm, myId), [dm, myId]);
    const peerLookup = usePeerName(peerId);
    const peer = peerLookup.data?.displayName
        ?? peerLookup.data?.email
        ?? peerId;

    const submit = () => {
        const trimmed = draft.trim();
        if (!trimmed) return;
        const html = `<p>${escapeHtml(trimmed).replace(/\n/g, "<br/>")}</p>`;
        send.mutate(html, { onSuccess: () => setDraft("") });
    };

    // Upload state — shared between the paperclip picker and image picker.
    // Voice/video payloads are attached via those buttons too. The dedicated
    // Phone / Video buttons below start a LIVE call, not a recording.
    const fileInputRef = useRef<HTMLInputElement>(null);
    const imageInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [callStarting, setCallStarting] = useState<"audio" | "video" | null>(null);

    const uploadAndSend = useCallback(async (blob: Blob, fileName: string, kind: "file" | "image" | "audio" | "video") => {
        if (!token) return;
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", new File([blob], fileName, { type: blob.type || "application/octet-stream" }));
            const res = await fetch(`${connectBaseUrl}/api/uploads`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: form,
                credentials: "omit"
            });
            if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
            const { objectKey, contentType, sizeBytes } = (await res.json()) as {
                objectKey: string; contentType: string; sizeBytes: number;
            };
            const url = `${connectBaseUrl}/api/uploads/${objectKey}`;
            const safe = escapeHtml(fileName);
            const html =
                kind === "image"
                    ? `<p><img src="${url}" alt="${safe}" style="max-width:240px;border-radius:6px;" /></p>`
                    : kind === "audio"
                        ? `<p>🎙️ <strong>${safe}</strong><br/><audio controls src="${url}" style="width:240px;"></audio></p>`
                        : kind === "video"
                            ? `<p>🎥 <strong>${safe}</strong><br/><video controls src="${url}" style="max-width:280px;border-radius:6px;"></video></p>`
                            : `<p>📎 <a href="${url}" target="_blank" rel="noreferrer noopener">${safe}</a> (${formatBytes(sizeBytes)}) — ${escapeHtml(contentType)}</p>`;
            send.mutate(html);
        } finally {
            setUploading(false);
        }
    }, [send, token]);

    const onPickFile: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        await uploadAndSend(f, f.name, "file");
        if (fileInputRef.current) fileInputRef.current.value = "";
    };
    const onPickImage: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        await uploadAndSend(f, f.name, "image");
        if (imageInputRef.current) imageInputRef.current.value = "";
    };

    // Live call: dispatch to CallDock which owns the actual meeting
    // create + WebRTC mesh + UI. Stays inside Mail (Gmail-style) — no
    // tab navigation. The receiver's CallDock listens for the BFF's
    // `meeting.invited` event and pops a ringing toast.
    const startCall = useCallback((kind: "audio" | "video") => {
        if (!token || callStarting) return;
        const peerEmail = peerLookup.data?.email?.trim();
        if (!peerEmail) {
            console.warn("Cannot start call — peer email unresolved");
            return;
        }
        setCallStarting(kind);
        try {
            window.dispatchEvent(new CustomEvent("athavita:startCall", {
                detail: { peerEmail, peerDisplayName: peer, kind }
            }));
        } finally {
            // CallDock has its own loading/calling state; we just need
            // to release the in-window guard so the user can re-issue.
            setTimeout(() => setCallStarting(null), 1500);
        }
    }, [callStarting, peer, peerLookup.data?.email, token]);

    const drag = useDraggable();

    return (
        <div
            className="pointer-events-auto mb-3 flex h-96 w-80 flex-col overflow-hidden rounded-lg border bg-card shadow-2xl"
            style={drag.moveProps.style}
        >
            <header
                className="flex items-center justify-between border-b px-3 py-2"
                {...drag.handleProps}
            >
                <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold">{peer}</div>
                    <div className="truncate text-[10px] text-muted-foreground">Drag to move</div>
                </div>
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={onMinimize}
                        aria-label="Minimize"
                        className="flex h-7 w-7 items-center justify-center rounded hover:bg-accent"
                    >
                        <Minus className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close chat"
                        className="flex h-7 w-7 items-center justify-center rounded hover:bg-accent"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </header>
            <div ref={scrollerRef} className="min-h-0 flex-1 overflow-auto p-3">
                {msgs.isPending ? (
                    <p className="text-sm text-muted-foreground">Loading…</p>
                ) : msgs.data && msgs.data.length > 0 ? (
                    <ul className="space-y-2">
                        {msgs.data.map((m) => (
                            <Bubble key={m.id} msg={m} />
                        ))}
                    </ul>
                ) : (
                    <p className="text-sm text-muted-foreground">Say hi!</p>
                )}
            </div>
            <form
                onSubmit={(e) => { e.preventDefault(); submit(); }}
                className="flex shrink-0 flex-col gap-1 border-t p-2"
            >
                <div className="flex items-end gap-2">
                    <textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                submit();
                            }
                        }}
                        rows={1}
                        placeholder="Type a message"
                        className="min-h-[36px] flex-1 resize-none rounded-md border p-2 text-sm"
                    />
                    <button
                        type="submit"
                        disabled={send.isPending || !draft.trim()}
                        className="flex h-8 items-center gap-1 rounded-md bg-primary px-3 text-xs text-primary-foreground disabled:opacity-50"
                    >
                        <Send className="h-3.5 w-3.5" />
                        Send
                    </button>
                </div>
                <div className="flex items-center gap-1">
                    <input ref={fileInputRef} type="file" hidden onChange={onPickFile} />
                    <input ref={imageInputRef} type="file" accept="image/*" hidden onChange={onPickImage} />
                    <DockTool
                        icon={<Paperclip className="h-3.5 w-3.5" />}
                        label="Attach file"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                    />
                    <DockTool
                        icon={<ImageIcon className="h-3.5 w-3.5" />}
                        label="Send image"
                        onClick={() => imageInputRef.current?.click()}
                        disabled={uploading}
                    />
                    <DockTool
                        icon={callStarting === "audio"
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <Phone className="h-3.5 w-3.5" />}
                        label="Audio call"
                        onClick={() => void startCall("audio")}
                        disabled={uploading || Boolean(callStarting)}
                    />
                    <DockTool
                        icon={callStarting === "video"
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <Video className="h-3.5 w-3.5" />}
                        label="Video call"
                        onClick={() => void startCall("video")}
                        disabled={uploading || Boolean(callStarting)}
                    />
                    {uploading ? <Loader2 className="ml-auto h-3.5 w-3.5 animate-spin text-muted-foreground" /> : null}
                </div>
            </form>
        </div>
    );
}

function DockTool({
    icon, label, onClick, disabled, active
}: {
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
    disabled?: boolean;
    active?: boolean;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-label={label}
            title={label}
            className={`flex h-7 w-7 items-center justify-center rounded ${active ? "bg-destructive/10" : "hover:bg-accent"} disabled:opacity-40`}
        >
            {icon}
        </button>
    );
}

function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function DmRow({ dm, onOpen }: { dm: ChatDm; onOpen: () => void }) {
    const auth = useAuth();
    const myId = normalizeUserId(auth.user?.profile.sub);
    const peerId = otherParticipantId(dm, myId);
    const peer = usePeerName(peerId);
    const label = peer.data?.displayName ?? peer.data?.email ?? peerId;
    return (
        <li>
            <button
                type="button"
                onClick={onOpen}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
            >
                <MessageCircle className="h-4 w-4 text-muted-foreground" />
                <div className="min-w-0 flex-1 truncate">{label}</div>
            </button>
        </li>
    );
}

function Bubble({ msg }: { msg: ChatMessage }) {
    return (
        <li className="flex flex-col">
            <div className="rounded-md bg-muted/60 p-2 text-sm">
                <div className="mb-0.5 text-[10px] font-semibold text-muted-foreground">
                    {msg.authorDisplayName ?? msg.authorEmail}
                </div>
                <div
                    className="prose prose-sm max-w-none dark:prose-invert"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(msg.bodyHtml) }}
                />
                <div className="mt-0.5 text-right text-[10px] text-muted-foreground">
                    {new Date(msg.sentAt).toLocaleTimeString()}
                </div>
            </div>
        </li>
    );
}

/**
 * Pick the "other" participant id — i.e. the one that isn't the signed-in
 * user. Falls back to participant A if we can't tell.
 */
function otherParticipantId(dm: ChatDm, me: string | undefined): string {
    const meN = normalizeUserId(me);
    const aN = normalizeUserId(dm.participantA);
    const bN = normalizeUserId(dm.participantB);
    if (meN && aN === meN) return dm.participantB;
    if (meN && bN === meN) return dm.participantA;
    return dm.participantA;
}

function escapeHtml(value: string): string {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}
